import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, User, Lock, BarChart3, Fuel, Play, UserPlus, LogIn } from 'lucide-react';
import BasicInfo from './steps/BasicInfo';
import PropertyDetails from './steps/PropertyDetails';
import OperationalMetrics from './steps/OperationalMetrics';
import ProfitCenters from './steps/ProfitCenters';
import FinancialMetrics from './steps/FinancialMetrics';
import ValuationResult from '../ValuationResult';
import LoadingScreen from './LoadingScreen';
import { useValuationForm } from '../../hooks/useValuationForm';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuthStore } from '../../store/useAuthStore';
import { useNavigate, Link } from 'react-router-dom';
import LoginModal from './LoginModal';
import DisclaimerModal from './DisclaimerModal';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { supabase } from '../../lib/supabase';
import { ValuationData, ValidationErrors } from '../../types/valuation';
import { apiLogger } from '../../utils/apiLogger';
import { getApiStatusMessage } from '../../utils/apiHealthCheck';
import { startPredictionTask, pollTaskUntilComplete, TaskStatus } from '../../utils/predictionService';
import { useDataFlywheel } from '../../hooks/useDataFlywheel';

// Error Boundary Component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught in ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h2>Oops, something went wrong. Please try again later.</h2>;
    }

    return this.props.children; 
  }
}

export default function ValuationWizard() {
  // Ref for scrolling to invalid fields
  const wizardContentRef = useRef<HTMLDivElement>(null);
  
  // Function to add the shake animation to invalid fields
  const highlightInvalidField = (fieldId: string) => {
    setTimeout(() => {
      let selector;
      
      // Determine which field to target based on fieldId
      if (fieldId === 'location') {
        selector = 'input[placeholder="Enter property address"]';
      } else if (fieldId === 'totalGrossProfit') {
        selector = '#totalGrossProfit';
      } else {
        // Generic selector by field ID
        selector = `#${fieldId}, [name="${fieldId}"]`;
      }
      
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        // First ensure we're scrolled to see the element
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Add shake animation
        element.classList.add('input-shake');
        
        // Add permanent invalid styling
        element.classList.add('input-invalid');
        
        // Also add required indicator to the label
        const labelElement = document.querySelector(`label[for="${fieldId}"]`) || 
                            element.closest('div')?.querySelector('label');
        if (labelElement) {
          labelElement.classList.add('required-field');
        }
        
        // Remove the shake animation after it completes
        setTimeout(() => {
          element.classList.remove('input-shake');
        }, 600); // Animation is 500ms
      }
    }, 100); // Short delay to ensure DOM is ready
  };

  const [currentStep, setCurrentStep] = useState(0);
  const flywheelSessionId = useRef(crypto.randomUUID());
  const [showResults, setShowResults] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showAuthGateModal, setShowAuthGateModal] = useState(false);
  const [predictedValue, setPredictedValue] = useState<number | null>(null);
  const [apiResponseData, setApiResponseData] = useState<any>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [isPollingStopped, setIsPollingStopped] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const { isProOrHigher } = useSubscription();
  const { isAuthenticated, user } = useAuthStore();
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const navigate = useNavigate();
  const {
    data,
    savedValuations,
    updateField,
    validateForm,
    saveValuation,
    loadValuation,
    saveProgress
  } = useValuationForm({
    // Basic Info
    location: '',
    propertyType: 'convenience-store', // Default to convenience store
    ownership: 'owned',
    leaseTermYears: undefined,
    brandAffiliation: 'unbranded',
    brandName: '',
    storeBrand: '',
    fuelSupplyYears: undefined,

    // Operational Metrics
    tankCapacity: 0,
    operatingHours: '24/7',
    specificHours: '',
    numberOfPumps: 0,
    fuelTypes: [],
    annualGallons: 0,
    fuelMargin: 0,

    // Property Details
    propertySize: 0,
    canopySize: 0, // This value will be synced with assets.Fueling_Canopy.size
    lotSize: 0,
    yearBuilt: 0, // 0 displays as empty in the form
    lastRenovated: undefined,
    renovationDescription: '',
    propertyCondition: 'Average',
    zoningType: 'Commercial', // Default to Commercial zoning for all properties
    trafficCount: undefined,
    assets: {
      Fueling_Canopy: {
        condition: 'Average',
        size: 0 // Will be updated with canopySize value in the PropertyDetails component
      },
      UST: {
        condition: 'Average',
        size: 0
      },
      Fueling_Equipment: {
        condition: 'Average',
        size: 0
      },
      Site_Improvements: {
        condition: 'Average',
        size: 0
      },
      Convenience_Store: {
        condition: 'Average',
        size: 0
      },
      Service_Station: {
        condition: 'Average',
        size: 0
      },
      FFE: {
        condition: 'Average',
        size: 0
      }
    },

    // Profit Centers
    hasCarWash: false,
    carWashType: 'automatic', // Default to automatic for all car washes
    hasLeasedQSR: false, // Default to no QSR
    additionalServices: [],

    // Financial Metrics
    storeRevenue: 0,
    storeMargin: undefined,
    carWashRevenue: undefined,
    carWashMargin: undefined,
    totalGrossProfit: undefined,

    // Operating Expenses
    operatingExpenses: 0,
    salaries: undefined,
    utilities: undefined,
    insurance: undefined,
    repairsAndMaintenance: undefined,
    propertyTax: undefined,
    advertisingAndPromotion: undefined,
    merchantFees: undefined,
    generalAndAdmin: undefined,
    isAnnual: false
  });

  // Data flywheel: capture financial inputs in real-time for model calibration
  const {
    captureOnStepTransition,
    logTamAccept,
    logTamDismiss,
    checkModifiedAfterAccept,
    markPaymentComplete,
  } = useDataFlywheel({
    data,
    isAuthenticated,
    userId: user?.id,
    sessionId: flywheelSessionId.current,
    tamResult: null, // TAM result lives inside FinancialMetrics; debounce/lifecycle use data
    currentStep,
  });

  // Component initialization complete
  
  // Add CSS for validation animations
  useEffect(() => {
    // Add the style element if it doesn't exist yet
    let styleElement = document.getElementById('validation-styles');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'validation-styles';
      document.head.appendChild(styleElement);
      
      // Define the CSS rules
      styleElement.innerHTML = `
        @keyframes shake {
          0% { transform: translateX(0); }
          20% { transform: translateX(-10px); }
          40% { transform: translateX(10px); }
          60% { transform: translateX(-5px); }
          80% { transform: translateX(5px); }
          100% { transform: translateX(0); }
        }
        .input-shake {
          animation: shake 0.5s ease-in-out;
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.3) !important;
        }
        .input-invalid {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.3) !important;
        }
        .required-field::after {
          content: '*';
          color: #ef4444;
          margin-left: 4px;
          font-weight: bold;
        }
      `;
    }
    
    // Clean up on unmount
    return () => {
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  // Track if we've already run the restoration to prevent multiple runs
  const hasRunRestoration = useRef(false);
  
  // Check for restored valuation data after OAuth flow
  useEffect(() => {
    const checkForRestoredData = () => {
      console.log('ValuationWizard: Checking for data restoration...');
      
      const oauthStartTime = sessionStorage.getItem('oauth_start_time');
      const valuationBackup = sessionStorage.getItem('valuation_backup');
      
      console.log('ValuationWizard: oauth_start_time:', oauthStartTime);
      console.log('ValuationWizard: valuation_backup exists:', !!valuationBackup);
      console.log('ValuationWizard: isAuthenticated:', isAuthenticated);
      
      // Only proceed if BOTH OAuth tracking AND backup data exist
      if (oauthStartTime && valuationBackup && isAuthenticated) {
        const startTime = parseInt(oauthStartTime, 10);
        const now = Date.now();
        const timeDiff = now - startTime;
        
        console.log('ValuationWizard: OAuth was', timeDiff, 'ms ago');
        
        // If OAuth flow was recent (within 5 minutes), restore the data
        if (timeDiff < 5 * 60 * 1000) {
          try {
            const backup = JSON.parse(valuationBackup);
            console.log('ValuationWizard: Restoring valuation data from backup:', backup);
            
            // Restore all the data in one batch to prevent re-renders
            const restoredData = { ...backup.data };
            console.log('ValuationWizard: Batch restoring all data:', restoredData);
            
            // Use a single updateField call to restore all data at once
            updateField(restoredData);
            
            // Restore the current step
            if (backup.currentStep !== undefined && backup.currentStep > 0) {
              console.log('ValuationWizard: Restoring current step:', backup.currentStep);
              setCurrentStep(backup.currentStep);
            }
            
            // Clear the backup and OAuth tracking immediately
            sessionStorage.removeItem('valuation_backup');
            sessionStorage.removeItem('oauth_start_time');
            
            toast.success('Valuation data restored after login!', {
              position: 'bottom-right',
              autoClose: 3000
            });
            
            // If the user was trying to get results, prompt for consent then redirect to Stripe.
            if (backup.wasRequestingResults) {
              console.log('ValuationWizard: User was requesting results after OAuth, prompting consent before checkout');
              // Small delay to let toast show, then open the consent modal.
              // initiateStripeCheckout() will handle the sessionStorage backup itself.
              setTimeout(() => {
                requestStripeCheckout();
              }, 1000);
            }
          } catch (error) {
            console.error('ValuationWizard: Error restoring valuation data:', error);
            sessionStorage.removeItem('valuation_backup');
            sessionStorage.removeItem('oauth_start_time');
          }
        } else {
          // Clear old backup data
          console.log('ValuationWizard: OAuth backup too old, clearing');
          sessionStorage.removeItem('valuation_backup');
          sessionStorage.removeItem('oauth_start_time');
        }
      } else {
        console.log('ValuationWizard: No OAuth backup data to restore or not authenticated');
      }
    };
    
    // Only run restoration check once when component mounts and is authenticated
    if (isAuthenticated && !hasRunRestoration.current) {
      hasRunRestoration.current = true;
      const timeoutId = setTimeout(() => {
        checkForRestoredData();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated]); // Keep dependency but add ref guard

  // Check for Stripe payment success/cancel on mount (runs once, no auth dependency)
  const hasCheckedPayment = useRef(false);
  useEffect(() => {
    if (hasCheckedPayment.current) return;

    const params = new URLSearchParams(window.location.search);
    const paymentSuccess = params.get('payment_success');
    const paymentCancelled = params.get('payment_cancelled');

    if (paymentSuccess === 'true') {
      hasCheckedPayment.current = true;

      const stripeSessionId = params.get('session_id');

      // Clean the URL without reloading
      window.history.replaceState({}, '', window.location.pathname);

      // Block if no session_id — possible spoofing attempt
      if (!stripeSessionId) {
        console.error('ValuationWizard: Payment success but no session_id in URL');
        toast.error('Payment verification failed: missing session ID. Please contact support.', {
          position: 'bottom-right',
          autoClose: 8000,
        });
        return;
      }

      const paymentStartTime = sessionStorage.getItem('payment_start_time');
      const valuationBackup = sessionStorage.getItem('valuation_backup');

      console.log('ValuationWizard: Payment success detected', {
        stripeSessionId,
        hasPaymentStartTime: !!paymentStartTime,
        hasValuationBackup: !!valuationBackup,
      });

      if (paymentStartTime && valuationBackup) {
        const startTime = parseInt(paymentStartTime, 10);
        const timeDiff = Date.now() - startTime;

        if (timeDiff < 30 * 60 * 1000) {
          try {
            const backup = JSON.parse(valuationBackup);
            console.log('ValuationWizard: Restoring data after payment, verifying...');

            // Restore form data immediately (UX: show form while verifying)
            updateField(backup.data);
            if (backup.currentStep !== undefined) {
              setCurrentStep(backup.currentStep);
            }

            // Clear backup so OAuth effect doesn't also try to use it
            sessionStorage.removeItem('valuation_backup');
            sessionStorage.removeItem('payment_start_time');

            // Server-side payment verification before running prediction
            const verifyAndRunPrediction = async () => {
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

              // Wait for auth to be ready (handles redirect race condition)
              let token: string | null = null;
              for (let attempt = 0; attempt < 10 && !token; attempt++) {
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                token = session?.access_token || null;
                console.log(
                  `ValuationWizard: post-payment auth probe attempt ${attempt + 1}/10`,
                  {
                    hasSession: !!session,
                    hasUser: !!session?.user,
                    userId: session?.user?.id,
                    expiresAt: session?.expires_at,
                    sessionError: sessionError?.message,
                  }
                );
                if (!token) await new Promise(r => setTimeout(r, 500));
              }

              if (!token) {
                console.error('ValuationWizard: post-payment auth probe exhausted — no token');
                throw new Error('Authentication not available. Please log in and try again.');
              }

              console.log('ValuationWizard: Verifying payment with Stripe session:', stripeSessionId);
              const response = await fetch(`${supabaseUrl}/functions/v1/verify-payment`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ session_id: stripeSessionId }),
              });

              const result = await response.json();
              if (!response.ok || !result.verified) {
                throw new Error(result.error || 'Payment verification failed');
              }

              const paymentId = result.payment_id;
              console.log('ValuationWizard: Payment verified, payment_id:', paymentId, 'triggering prediction');

              if (!paymentId) {
                throw new Error('Payment was verified but no payment ID was returned. Please redeploy the verify-payment edge function or contact support.');
              }
              toast.success('Payment verified! Generating your valuation report...', {
                position: 'bottom-right',
                autoClose: 3000,
              });

              // Data flywheel: mark all captures in this session as converted
              const savedFlywheelSessionId = sessionStorage.getItem('flywheel_session_id');
              if (savedFlywheelSessionId) {
                markPaymentComplete();
                sessionStorage.removeItem('flywheel_session_id');
              }

              if (backup.data.location && backup.data.location.trim() !== '') {
                await makePredictionRequestWithData(backup.data, paymentId);
              }
            };

            verifyAndRunPrediction().catch((error: any) => {
              console.error('ValuationWizard: Payment verification failed:', error);
              toast.error(
                `Payment verification failed: ${error.message}. If you were charged, please contact support.`,
                { position: 'bottom-right', autoClose: 8000 }
              );
            });
          } catch (error) {
            console.error('ValuationWizard: Error in payment success flow:', error);
            sessionStorage.removeItem('valuation_backup');
            sessionStorage.removeItem('payment_start_time');
          }
        } else {
          console.log('ValuationWizard: Payment backup too old, clearing');
          sessionStorage.removeItem('valuation_backup');
          sessionStorage.removeItem('payment_start_time');
        }
      }
    } else if (paymentCancelled === 'true') {
      hasCheckedPayment.current = true;

      // Clean the URL
      window.history.replaceState({}, '', window.location.pathname);

      // Restore form data so user doesn't lose their work
      const valuationBackup = sessionStorage.getItem('valuation_backup');
      if (valuationBackup) {
        try {
          const backup = JSON.parse(valuationBackup);
          updateField(backup.data);
          if (backup.currentStep !== undefined) {
            setCurrentStep(backup.currentStep);
          }
          sessionStorage.removeItem('valuation_backup');
          sessionStorage.removeItem('payment_start_time');
        } catch (error) {
          console.error('ValuationWizard: Error restoring data after cancel:', error);
        }
      }

      toast.info('Payment was cancelled. You can try again when ready.', {
        position: 'bottom-right',
        autoClose: 4000,
      });
    }
  }, []); // Run once on mount — no auth dependency needed

  const goToNextStep = () => {
    console.log('=== goToNextStep called ===');
    console.log('Current step:', currentStep);
    console.log('isAuthenticated:', isAuthenticated);
    console.log('showAuthGateModal:', showAuthGateModal);
    
    // For step 0 (BasicInfo), check authentication FIRST before validation
    if (currentStep === 0) {
      console.log('Step 0 -> 1: Checking authentication. isAuthenticated:', isAuthenticated);
      if (!isAuthenticated) {
        console.log('User not authenticated, showing auth gate modal');
        setShowAuthGateModal(true);
        return;
      }
      console.log('User authenticated, proceeding with validation');
    }
    
    // Always validate the current step before proceeding
    const currentStepValidation = validateCurrentStep();
    
    console.log('Validation result:', currentStepValidation);
    
    if (!currentStepValidation.isValid) {
      console.log('Validation failed, highlighting field:', currentStepValidation.fieldId);
      
      // Show user-friendly validation message
      alert(currentStepValidation.message);
      
      // Update validation errors for form highlighting
      if (!data.validationErrors) {
        updateField({ validationErrors: { [currentStepValidation.fieldId]: currentStepValidation.message } });
      } else if (!data.validationErrors[currentStepValidation.fieldId]) {
        updateField({ validationErrors: { ...data.validationErrors, [currentStepValidation.fieldId]: currentStepValidation.message } });
      }
      return;
    }
    
    // After validation passes, proceed to next step or show disclaimer
    if (currentStep === 0) {
      console.log('User authenticated and validation passed, showing disclaimer modal');
      setShowDisclaimerModal(true);
    } else {
      // For all other steps, proceed normally
      console.log('Moving from step', currentStep, 'to', currentStep + 1);
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
      saveProgress();

      // Scroll to top of the page when moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDisclaimerProceed = () => {
    setShowDisclaimerModal(false);
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    saveProgress();

    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousStep = () => {
    if (currentStep === 4) captureOnStepTransition('step_previous');
    setCurrentStep((prev) => Math.max(prev - 1, 0));

    // Scroll to top of the page when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const steps = useMemo(() => [
    {
      title: 'Basic Information',
      component: <BasicInfo data={data} onUpdate={updateField} errors={errors} />
    },
    {
      title: 'Property Details',
      component: <PropertyDetails data={data} onUpdate={updateField} errors={errors} />
    },
    {
      title: 'Operational Metrics',
      component: <OperationalMetrics data={data} onUpdate={updateField} errors={errors} />
    },
    {
      title: 'Profit Centers',
      component: <ProfitCenters data={data} onUpdate={updateField} errors={errors} />
    },
    {
      title: 'Financial Metrics',
      component: <FinancialMetrics
        data={data}
        onUpdate={updateField}
        errors={errors}
        onNext={goToNextStep}
        onPrevious={goToPreviousStep}
        logTamAccept={logTamAccept}
        logTamDismiss={logTamDismiss}
        checkModifiedAfterAccept={checkModifiedAfterAccept}
      />
    }
  ], [data, updateField, errors, goToNextStep, goToPreviousStep, logTamAccept, logTamDismiss, checkModifiedAfterAccept]);

  // Handle keyboard navigation for Pro users
  useEffect(() => {
    if (!isProOrHigher()) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && currentStep < steps.length - 1) {
        goToNextStep();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        goToPreviousStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, isProOrHigher]);

  // Function to validate the current step based on its specific requirements
  const validateCurrentStep = (): { isValid: boolean; message: string; fieldId: string } => {
    let isValid = true;
    let message = '';
    let fieldId = '';
    
    // Basic Info step validation (Step 0)
    if (currentStep === 0) {
      // Required: location
      if (!data.location || data.location.trim() === '') {
        isValid = false;
        message = 'Property address is required';
        fieldId = 'location';
      }
      // Required: propertyType
      else if (!data.propertyType || data.propertyType.trim() === '') {
        isValid = false;
        message = 'Property type is required';
        fieldId = 'propertyType';
      }
      // Required: brandAffiliation
      else if (!data.brandAffiliation) {
        isValid = false;
        message = 'Brand affiliation is required';
        fieldId = 'brandAffiliation';
      }
      // If branded, brandName is required
      else if (data.brandAffiliation === 'branded' && (!data.brandName || data.brandName.trim() === '')) {
        isValid = false;
        message = 'Fuel brand is required for branded stations';
        fieldId = 'brandName';
      }
      // Check for validation errors from address lookup
      else if (data.validationErrors?.location) {
        isValid = false;
        message = data.validationErrors.location;
        fieldId = 'location';
      }
      
      if (!isValid) return { isValid, message, fieldId };
    }
    
    // Property Details step validation (Step 1)
    if (currentStep === 1) {
      // Required: propertySize
      if (!data.propertySize || data.propertySize <= 0) {
        isValid = false;
        message = 'Property size is required and must be greater than 0';
        fieldId = 'propertySize';
      }
      // Required: lotSize
      else if (!data.lotSize || data.lotSize <= 0) {
        isValid = false;
        message = 'Lot size is required and must be greater than 0';
        fieldId = 'lotSize';
      }
      // Required: yearBuilt
      else if (!data.yearBuilt || data.yearBuilt <= 0) {
        isValid = false;
        message = 'Year built is required';
        fieldId = 'yearBuilt';
      }
      // Required: zoningType
      else if (!data.zoningType || data.zoningType.trim() === '') {
        isValid = false;
        message = 'Zoning type is required';
        fieldId = 'zoningType';
      }
      // Required: assets (at least one asset must be defined)
      else if (!data.assets || Object.keys(data.assets).length === 0) {
        isValid = false;
        message = 'At least one asset condition must be specified';
        fieldId = 'assets';
      }
      
      if (!isValid) return { isValid, message, fieldId };
    }
    
    // Operational Metrics step validation (Step 2)
    if (currentStep === 2) {
      // Required: operatingHours
      if (!data.operatingHours || data.operatingHours.trim() === '') {
        isValid = false;
        message = 'Operating hours are required';
        fieldId = 'operatingHours';
      }
      // Required: numberOfPumps
      else if (!data.numberOfPumps || data.numberOfPumps <= 0) {
        isValid = false;
        message = 'Number of pumps is required and must be greater than 0';
        fieldId = 'numberOfPumps';
      }
      // Required: fuelTypes (must have at least one)
      else if (!data.fuelTypes || data.fuelTypes.length === 0) {
        isValid = false;
        message = 'At least one fuel type must be selected';
        fieldId = 'fuelTypes';
      }
      
      if (!isValid) return { isValid, message, fieldId };
    }
    
    // Profit Centers step validation (Step 3)
    if (currentStep === 3) {
      // Required: hasCarWash (boolean, so check if it's been set)
      if (data.hasCarWash === undefined || data.hasCarWash === null) {
        isValid = false;
        message = 'Please specify if the station has a car wash';
        fieldId = 'hasCarWash';
      }
      // Required: additionalServices (array, can be empty but must exist)
      else if (!data.additionalServices) {
        isValid = false;
        message = 'Additional services selection is required (can be none)';
        fieldId = 'additionalServices';
      }
      
      if (!isValid) return { isValid, message, fieldId };
    }
    
    // Financial Metrics step validation (Step 4)
    if (currentStep === 4) {
      // Required: storeRevenue
      if (!data.storeRevenue || data.storeRevenue < 0) {
        isValid = false;
        message = 'Store revenue is required and must be 0 or greater';
        fieldId = 'storeRevenue';
      }
      // Required: operatingExpenses
      else if (!data.operatingExpenses || data.operatingExpenses < 0) {
        isValid = false;
        message = 'Operating expenses is required and must be 0 or greater';
        fieldId = 'operatingExpenses';
      }
      // Check totalGrossProfit for final validation
      else if (!data.totalGrossProfit || data.totalGrossProfit <= 0) {
        isValid = false;
        message = 'Total Gross Profit is required to proceed with valuation';
        fieldId = 'totalGrossProfit';
      }
      // Margin required when revenue/volume is entered
      else if (data.annualGallons && data.annualGallons > 0 && !data.fuelMargin) {
        isValid = false;
        message = 'Fuel margin is required when fuel volume is entered';
        fieldId = 'fuelMargin';
      }
      else if (data.storeRevenue && data.storeRevenue > 0 && !data.storeMargin) {
        isValid = false;
        message = 'C-Store margin is required when store revenue is entered';
        fieldId = 'storeMargin';
      }
      else if (data.hasCarWash && data.carWashRevenue && data.carWashRevenue > 0 && !data.carWashMargin) {
        isValid = false;
        message = 'Car wash margin is required when car wash revenue is entered';
        fieldId = 'carWashMargin';
      }
      else if (data.qsrRepairBayRevenue && data.qsrRepairBayRevenue > 0 && !data.qsrRepairBayMargin) {
        isValid = false;
        message = 'Additional revenue margin is required when revenue is entered';
        fieldId = 'qsrRepairBayMargin';
      }

      if (!isValid) return { isValid, message, fieldId };
    }
    
    return { isValid, message, fieldId };
  };

  // Error state tracking for different API issues
  const [apiError, setApiError] = useState<{
    message: string;
    details: string;
    type: 'timeout' | 'server' | 'connection' | 'auth' | 'unknown';
  } | null>(null);
  
  // Handles polling for task status with progressive intervals
  useEffect(() => {
    let pollingTimeoutId: number | undefined;
    
    // Progressive polling with increasing intervals
    const getPollingInterval = (attempts: number): number => {
      // Start with shorter intervals that gradually increase
      // 500ms -> 1000ms -> 1500ms -> ... up to 5000ms max
      const baseInterval = 500; // Start with 500ms
      const increment = 500;    // Increase by 500ms each time
      const maxInterval = 5000; // Cap at 5 seconds
      
      const calculatedInterval = baseInterval + (attempts * increment);
      return Math.min(calculatedInterval, maxInterval);
    };
    
    const pollStatus = async () => {
      if (!taskStatus?.task_id || isPollingStopped) return;
      
      try {
        const baseUrl = import.meta.env.VITE_PREDICTION_URL;
        const storeToken = useAuthStore.getState().token;
        const { data: { session } } = await supabase.auth.getSession();
        const token = storeToken || session?.access_token;
        
        if (!token) {
          setApiError({
            message: 'Authentication error during polling',
            details: 'No authentication token found',
            type: 'auth' as const
          });
          setIsPollingStopped(true);
          return;
        }
        
        // Make a single status check
        const statusUrl = `${baseUrl}/analyze-station/status/${taskStatus.task_id}`;
        
        const response = await axios.get(statusUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        const updatedStatus = response.data as TaskStatus;
        setTaskStatus(updatedStatus);
        
        // Update polling counter for progressive intervals
        setPollAttempts(prev => prev + 1);
        
        const currentAttempt = pollAttempts + 1;
        const interval = getPollingInterval(currentAttempt);
        
        apiLogger.debug('Task status update', { 
          taskId: updatedStatus.task_id,
          status: updatedStatus.status,
          pollAttempt: currentAttempt,
          nextPollIn: `${interval}ms`
        });
        
        // If the task is complete, process the result
        if (updatedStatus.status === 'completed' && updatedStatus.result) {
          setIsPollingStopped(true);
          
          const predictionResponse = updatedStatus.result;
          
          // Log API response using our utility
          apiLogger.log('API Response [ValuationPrediction]', predictionResponse);
          
          // Check if response has the expected structure
          if (!predictionResponse.result || !predictionResponse.result.total_station_value) {
            setApiError({
              message: 'Invalid API response structure',
              details: 'The API response did not include result.total_station_value property',
              type: 'unknown' as const
            });
            return;
          }
    
          // Extract the correct prediction value from the response structure
          const predictedValue = predictionResponse.result.total_station_value;

          // Call TAM Edge Function for detailed margin analysis
          console.log('[ValuationWizard] Calling TAM Edge Function for detailed analysis...');
          try {
            const tamInput: any = {
              station_object_id: data.stationObjectId || undefined,
              fuel_brand: data.brandAffiliation === 'branded' ? data.brandName : null,
              county: data.location ? data.location.split(',')[0]?.trim() : null,
              state: 'CA',
              adt: data.trafficCount || null,
              dispensers: data.numberOfPumps || null,
              store_size: data.propertySize || null,
            };

            console.log('[ValuationWizard] TAM input:', tamInput);

            const { data: tamData, error: tamError } = await supabase.client.functions.invoke('tam-simple', {
              body: tamInput
            });

            if (tamError) {
              console.error('[ValuationWizard] TAM Edge Function error:', tamError);
              // Don't fail the whole flow - just log the error
              apiLogger.warn('TAM estimation failed but continuing with ML prediction', { error: tamError });
            } else if (tamData) {
              console.log('[ValuationWizard] TAM Edge Function response:', tamData);
              console.log('[ValuationWizard] TAM data keys:', Object.keys(tamData));
              console.log('[ValuationWizard] TAM has estimates?', !!tamData.estimates);
              console.log('[ValuationWizard] TAM has diagnostics?', !!tamData.diagnostics);
              console.log('[ValuationWizard] TAM diagnostics keys:', tamData.diagnostics ? Object.keys(tamData.diagnostics) : 'N/A');

              // Merge TAM data into the prediction response
              predictionResponse.result.tam_estimation = tamData;
              console.log('[ValuationWizard] After merge - predictionResponse.result keys:', Object.keys(predictionResponse.result));
              console.log('[ValuationWizard] After merge - has tam_estimation?', !!predictionResponse.result.tam_estimation);
            }
          } catch (tamErr) {
            console.error('[ValuationWizard] Error calling TAM Edge Function:', tamErr);
            // Continue with ML prediction even if TAM fails
            apiLogger.warn('TAM estimation error but continuing with ML prediction', { error: tamErr });
          }

          // Store the complete API response (now with TAM data) for use in ValuationResult
          setApiResponseData(predictionResponse);
          console.log('[ValuationWizard] setApiResponseData called with prediction response');
          console.log('[ValuationWizard] Response has tam_estimation?', !!predictionResponse.result.tam_estimation);

          // Log the extracted value
          apiLogger.debug('Extracted prediction value', {
            totalStationValue: predictedValue,
            businessValue: predictionResponse.result.predicted_business_value,
            landValue: predictionResponse.result.predicted_land_value,
            hasTamEstimation: !!predictionResponse.result.tam_estimation
          });
          
          if (isNaN(predictedValue)) {
            setApiError({
              message: 'Invalid prediction value',
              details: `The API returned an invalid prediction value: ${predictedValue}`,
              type: 'unknown' as const
            });
            return;
          }
    
          console.log('Setting predicted value:', predictedValue);
          setPredictedValue(predictedValue);
          setShowResults(true);
          setIsLoading(false);
          
          // Show completion message
          toast.success('Analysis complete!', {
            position: 'bottom-right',
            autoClose: 3000
          });
        }
        
        // If the task failed, show an error
        else if (updatedStatus.status === 'failed') {
          setIsPollingStopped(true);
          setIsLoading(false);
          
          setApiError({
            message: 'Analysis failed',
            details: updatedStatus.error || 'The analysis task failed with no error details',
            type: 'server'
          });
          
          // Show error toast
          toast.error(`Analysis failed: ${updatedStatus.error || 'Unknown error'}`, {
            position: 'bottom-right',
            autoClose: 5000
          });
        }
        // For pending or in_progress status, schedule next poll with progressive interval
        else if (!isPollingStopped) {
          pollingTimeoutId = window.setTimeout(pollStatus, interval);
        }
      } catch (error) {
        apiLogger.error('Error polling for task status', error);
        
        // Don't stop polling on network errors, just log them and retry with increased interval
        if (axios.isAxiosError(error) && error.code === 'ERR_NETWORK') {
          apiLogger.warn('Network error during polling - will retry');
          // Even for network errors, increase the poll interval to avoid hammering the server
          setPollAttempts(prev => prev + 1);
          const interval = getPollingInterval(pollAttempts + 1);
          pollingTimeoutId = window.setTimeout(pollStatus, interval);
        } else {
          setIsPollingStopped(true);
          setIsLoading(false);
          
          setApiError({
            message: 'Polling error',
            details: axios.isAxiosError(error) ? error.message : 'Unknown error during status polling',
            type: 'connection'
          });
        }
      }
    };

    // Start polling if we have a task ID
    if (taskStatus?.task_id && !isPollingStopped) {
      // Initial poll immediately
      pollStatus();
    }

    return () => {
      // Clear any pending timeout on unmount
      if (pollingTimeoutId) {
        window.clearTimeout(pollingTimeoutId);
      }
    };
  }, [taskStatus, isPollingStopped, pollAttempts]);

  // Utility function to clean up assets before sending to API
  const cleanAssetsForApi = (data: ValuationData) => {
    if (!data.assets) return {};

    const cleanedAssets: any = {};

    // Universal assets that apply to all gas stations - always include these essential assets
    const universalAssets = ['Fueling_Canopy', 'UST', 'Fueling_Equipment', 'Site_Improvements', 'FFE'];

    universalAssets.forEach(assetKey => {
      if (data.assets[assetKey]) {
        // Always include essential assets regardless of size or condition
        // The ML service needs these for proper valuation calculations
        cleanedAssets[assetKey] = data.assets[assetKey];
      }
    });

    // Property type specific asset (always include the one matching the selected property type)
    const propertyTypeAssets = {
      'convenience-store': 'Convenience_Store',
      'service-station': 'Service_Station',
      'kiosk': 'Kiosk',
      'food-booth': 'Food_Booth'
    };

    const selectedBuildingAsset = propertyTypeAssets[data.propertyType as keyof typeof propertyTypeAssets];
    if (selectedBuildingAsset && data.assets[selectedBuildingAsset]) {
      // Always include the building asset for the selected property type
      cleanedAssets[selectedBuildingAsset] = data.assets[selectedBuildingAsset];
    }

    // Car wash assets (include if car wash data exists, regardless of size)
    if (data.hasCarWash || data.assets.Car_Wash || data.assets.Car_Wash_Equipment) {
      if (data.assets.Car_Wash) {
        cleanedAssets.Car_Wash = data.assets.Car_Wash;
      }
      if (data.assets.Car_Wash_Equipment) {
        cleanedAssets.Car_Wash_Equipment = data.assets.Car_Wash_Equipment;
      }
    }

    return cleanedAssets;
  };

  const makePredictionRequestWithData = async (requestData: ValuationData, paymentId?: string) => {
    setIsLoading(true);
    setApiError(null);
    setTaskStatus(null);
    setIsPollingStopped(false);
    
    try {
      // Enhanced validation of form data
      console.log('ValuationWizard: Validating data for prediction request:', requestData);
      
      if (!requestData) {
        const error = {
          message: 'Missing required data',
          details: 'Form data is required for valuation prediction',
          type: 'unknown' as const
        };
        setApiError(error);
        throw new Error(error.message);
      }
      
      if (!requestData.location || requestData.location.trim() === '') {
        const error = {
          message: 'Missing required data',
          details: 'Property location is required for valuation prediction',
          type: 'unknown' as const
        };
        setApiError(error);
        throw new Error(error.message);
      }
      
      // Validate other critical fields
      if (!requestData.totalGrossProfit || requestData.totalGrossProfit <= 0) {
        const error = {
          message: 'Missing required data',
          details: 'Total Gross Profit is required for valuation prediction',
          type: 'unknown' as const
        };
        setApiError(error);
        throw new Error(error.message);
      }
      
      // Check API health before making the request
      const baseUrl = import.meta.env.VITE_PREDICTION_URL;
      const apiStatus = await getApiStatusMessage(baseUrl);
      
      apiLogger.debug('API Status', { status: apiStatus });
      apiLogger.debug('Starting asynchronous valuation prediction', {
        endpoint: 'analyze-station',
        requestData
      });

      // Get authentication token
      const storeToken = useAuthStore.getState().token;
      const { data: { session } } = await supabase.auth.getSession();
      const token = storeToken || session?.access_token;
      
      if (!token) {
        const error = {
          message: 'Authentication error',
          details: 'No authentication token found',
          type: 'auth' as const
        };
        setApiError(error);
        throw new Error(error.message);
      }
      
      // Clean the request data to only include relevant assets.
      // Food service fields were removed from the frontend form, but the
      // regressor's Pydantic model still requires them — send zero/empty
      // defaults so the schema validates. The dev backend additionally
      // requires a top-level `hasFoodService` boolean.
      const legacyFoodServiceDefaults = {
        hasFoodService: false,
        foodServiceType: [],
        foodServiceOwnership: 'owned',
        foodServiceLeaseYears: 0,
        foodServiceRent: 0,
        foodServiceRevenue: 0,
        foodServiceMargin: 0,
      };
      const cleanedRequestData = {
        ...legacyFoodServiceDefaults,
        ...requestData,
        assets: cleanAssetsForApi(requestData),
        ...(paymentId ? { payment_id: paymentId } : {}),
      };

      console.log('Cleaned assets for API:', cleanedRequestData.assets);

      // Start an asynchronous prediction task
      const task = await startPredictionTask(
        baseUrl,
        'analyze-station',
        cleanedRequestData,
        token,
        {
          timeout: 30000,  // 30 second timeout for initial request
          retries: 1       // Try once, then retry once if it fails
        }
      );
      
      // Set task status to initiate polling
      setTaskStatus(task);
      
      // Log the task ID
      apiLogger.log('Prediction task started', { 
        taskId: task.task_id, 
        status: task.status,
        statusUrl: task.status_url
      });
      
      // Show status message to user
      toast.info(
        'Analysis started! This may take 1-2 minutes to complete.',
        { position: 'bottom-right', autoClose: 5000 }
      );
      
      // The rest of the process happens in the polling effect
      
    } catch (error: any) {
      // Provide more specific error messages based on error type
      apiLogger.error('Failed to start prediction task', error);
      
      // If we haven't already set an apiError in the try block
      if (!apiError) {
        if (axios.isAxiosError(error)) {
          if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            setApiError({
              message: 'Request timed out',
              details: 'The API request timed out. The server might be busy or unresponsive.',
              type: 'timeout'
            });
          } else if (error.response) {
            // Server responded with non-2xx status code
            setApiError({
              message: `Server error: ${error.response.status}`,
              details: `The server responded with status ${error.response.status}: ${JSON.stringify(error.response.data)}`,
              type: 'server'
            });
          } else if (error.request) {
            // Request made but no response received
            setApiError({
              message: 'Connection error',
              details: 'The request was made but no response was received. Check your network connection.',
              type: 'connection'
            });
          } else {
            setApiError({
              message: 'Request error',
              details: error.message,
              type: 'unknown'
            });
          }
        } else {
          setApiError({
            message: 'Unknown error',
            details: error.message || 'An unknown error occurred during the API request',
            type: 'unknown'
          });
        }
      }
      
      // Show detailed error alert with information to help debug the issue
      const errorDetail = apiError ? `${apiError.message}: ${apiError.details}` : 'Unknown error';
      alert(`Error starting analysis: ${errorDetail}`);
      setIsLoading(false);
    }
    
    // Note: We don't set isLoading to false here because polling will continue
    // The loading state will be cleared when polling completes or fails
  };

  const makePredictionRequest = async () => {
    return makePredictionRequestWithData(data);
  };

  // Open the consent modal. Called by both the direct click-Finish path and the
  // post-OAuth restoration path. Nothing actually charges until the user checks the
  // box and clicks Pay, which calls initiateStripeCheckout().
  const requestStripeCheckout = () => {
    setConsentChecked(false);
    setShowConsentModal(true);
  };

  // Stripe Checkout: redirect user to Stripe payment page. Only called after the
  // user has affirmatively accepted Terms / Privacy / Refund Policy in the modal.
  const initiateStripeCheckout = async () => {
    setIsPaymentProcessing(true);
    try {
      // Only save backup if one doesn't already exist (e.g., from OAuth→Stripe flow)
      if (!sessionStorage.getItem('payment_start_time')) {
        const paymentBackup = {
          data,
          currentStep,
          timestamp: Date.now(),
          version: '1.0',
          wasRequestingResults: true,
          paymentPending: true,
        };
        sessionStorage.setItem('valuation_backup', JSON.stringify(paymentBackup));
        sessionStorage.setItem('payment_start_time', Date.now().toString());
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          userId: user?.id || '',
          userEmail: user?.email || '',
          propertyAddress: data.location || '',
          successUrl: `${window.location.origin}/valuation?payment_success=true`,
          cancelUrl: `${window.location.origin}/valuation?payment_cancelled=true`,
          termsAccepted: true,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.url) {
        throw new Error(result.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = result.url;
    } catch (error: any) {
      console.error('Stripe checkout error:', error);
      toast.error('Payment processing failed. Please try again.', {
        position: 'bottom-right',
        autoClose: 5000,
      });
      // Clean up on failure
      sessionStorage.removeItem('payment_start_time');
      setIsPaymentProcessing(false);
    }
  };

  const handleFinish = async () => {
    // Validate the current step before finishing
    const validation = validateCurrentStep();

    if (!validation.isValid) {
      // Show validation error message
      alert(validation.message);

      // Update validation errors for form highlighting
      if (!data.validationErrors) {
        updateField({ validationErrors: { [validation.fieldId]: validation.message } });
      } else if (!data.validationErrors[validation.fieldId]) {
        updateField({ validationErrors: { ...data.validationErrors, [validation.fieldId]: validation.message } });
      }
      return;
    }

    captureOnStepTransition('view_results');
    saveProgress();

    // Save flywheel session ID so payment success flow can mark captures as converted
    sessionStorage.setItem('flywheel_session_id', flywheelSessionId.current);

    // Save the complete valuation data to sessionStorage as backup
    const valuationBackup = {
      data,
      currentStep,
      timestamp: Date.now(),
      version: '1.0',
      wasRequestingResults: true  // Flag to indicate user was requesting results
    };
    sessionStorage.setItem('valuation_backup', JSON.stringify(valuationBackup));

    if (!isAuthenticated) {
      console.log('ValuationWizard: User not authenticated, showing login modal');
      setShowLoginModal(true);
    } else {
      console.log('ValuationWizard: User authenticated, prompting consent before Stripe checkout');
      requestStripeCheckout();
    }
  };

  const handleLoginSuccess = async () => {
    setShowLoginModal(false);
    // After login, the restoration effect should handle the prediction automatically
    // No need to trigger it manually here
  };

  const handleAuthGateProceed = () => {
    setShowAuthGateModal(false);
    navigate('/login');
  };

  if (showResults) {
    console.log('Passing predictedValue to ValuationResult:', predictedValue);
    return <ValuationResult 
      data={data} 
      predictedValue={predictedValue}
      apiResponse={apiResponseData}
      onBack={() => setCurrentStep(steps.length - 1)} 
    />;
  }
  
  // Loading overlay - shown when fetching regression results
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Description text - Only shown on first step */}
      {currentStep === 0 && (
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Gas Station Valuation</h2>
          <p className="text-gray-600">Complete this form to get an estimate of your gas station's value</p>
        </div>
      )}
      
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`text-sm font-medium ${
                index <= currentStep ? 'text-fc-orange-500' : 'text-gray-400'
              }`}
            >
              {step.title}
            </div>
          ))}
        </div>
        <div className="h-2 bg-gray-200 rounded-full">
          <div
            className="h-full bg-fc-orange-500 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          ref={wizardContentRef}
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {steps[currentStep].component}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={goToPreviousStep}
          disabled={currentStep === 0}
          className={`flex items-center px-4 py-2 rounded-lg ${
            currentStep === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-fc-orange-50 text-fc-orange-600 hover:bg-fc-orange-100'
          }`}
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Previous
        </button>

        {currentStep < steps.length - 1 ? (
          <button
            onClick={goToNextStep}
            className="flex items-center px-4 py-2 bg-fc-orange-500 text-white rounded-lg hover:bg-fc-orange-600"
          >
            Next
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        ) : (
          <button
            onClick={handleFinish}
            disabled={isPaymentProcessing || isLoading}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed"
          >
            {isPaymentProcessing ? (
              <>
                <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Processing...
              </>
            ) : (
              'View Results'
            )}
          </button>
        )}
      </div>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
        valuationId={taskStatus?.task_id}
      />
      
      <DisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
        onProceed={handleDisclaimerProceed}
      />
      
      <AuthGateModal
        isOpen={showAuthGateModal}
        onClose={() => setShowAuthGateModal(false)}
        onProceed={handleAuthGateProceed}
        navigate={navigate}
      />

      {showConsentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Confirm purchase</h2>
            <p className="text-gray-700 mb-4">
              You're about to purchase a single Fuel Central valuation. Your purchase
              covers <strong>one valuation run</strong> — your inputs cannot be edited
              after you submit. Estimates are not certified appraisals — please review
              our policies before continuing.
            </p>

            <label className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-md cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 h-4 w-4 text-fc-orange-500 border-gray-300 rounded focus:ring-fc-orange-500"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</Link>,{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</Link>, and{' '}
                <Link to="/refund-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Refund Policy</Link>.
                I understand the valuation is an estimate and not an appraisal.
              </span>
            </label>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowConsentModal(false);
                  setConsentChecked(false);
                  // Clear any OAuth-leftover payment state so the next attempt starts clean.
                  sessionStorage.removeItem('payment_start_time');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!consentChecked || isPaymentProcessing}
                onClick={async () => {
                  setShowConsentModal(false);
                  await initiateStripeCheckout();
                }}
                className="px-4 py-2 text-white bg-fc-orange-500 rounded-md hover:bg-fc-orange-600 disabled:bg-fc-orange-300 disabled:cursor-not-allowed"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styling will be added via CSS in useEffect */}
    </div>
  );
}

// Authentication Gate Modal Component
const AuthGateModal = ({ 
  isOpen, 
  onClose, 
  onProceed, 
  navigate 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onProceed: () => void;
  navigate: (path: string) => void;
}) => {
  console.log('AuthGateModal render - isOpen:', isOpen);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-fc-orange-100 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-fc-orange-500" />
          </div>
          
          <h3 className="text-2xl font-semibold mb-3">Authentication Required</h3>
          <p className="text-gray-600 mb-6">
            To continue with your valuation, please sign in to your account or create a new one. 
            You can also view our interactive demo to see how the process works.
          </p>
          
          <div className="flex flex-col space-y-3 w-full">
            <button
              onClick={onProceed}
              className="flex items-center justify-center px-6 py-3 bg-fc-orange-50 border-2 border-fc-orange-200 text-fc-orange-800 rounded-lg hover:bg-fc-orange-100 hover:border-fc-orange-300 transition-colors font-semibold"
            >
              <LogIn className="w-5 h-5 mr-2 text-fc-orange-500" />
              Sign In
            </button>

            <button
              onClick={() => {
                onClose();
                navigate('/signup');
              }}
              className="flex items-center justify-center px-6 py-3 bg-green-50 border-2 border-green-200 text-green-800 rounded-lg hover:bg-green-100 hover:border-green-300 transition-colors font-semibold"
            >
              <UserPlus className="w-5 h-5 mr-2 text-green-600" />
              Create Account
            </button>

            <button
              onClick={() => {
                onClose();
                navigate('/valuation-demo');
              }}
              className="flex items-center justify-center px-6 py-3 bg-orange-50 border-2 border-orange-200 text-orange-800 rounded-lg hover:bg-orange-100 hover:border-orange-300 transition-colors font-semibold"
            >
              <Play className="w-5 h-5 mr-2 text-orange-600" />
              View Interactive Demo
            </button>

            <button
              onClick={onClose}
              className="mt-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};